// Self-contained demo messages for diagnosing the hosted renderer at
// /overlay/<anything>?demo=1 — exercises a paint name, badges, emotes, an event,
// and an atmosphere without needing a live feed or the identity API.

import type { OverlayMessage, OverlayPaint, OverlayAtmosphere } from './overlayMessage';

const hex = (h: string) => {
  const n = parseInt(h.slice(1), 16);
  return { hex: h, r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255, a: 1 };
};

const NEBULA: OverlayPaint = {
  id: 'demo-nebula',
  name: 'Nebula',
  data: {
    layers: [
      {
        id: 'l0',
        opacity: 1,
        ty: {
          __typename: 'PaintLayerTypeLinearGradient',
          angle: 120,
          stops: [
            { at: 0, color: hex('#8a5cff') },
            { at: 0.5, color: hex('#ff5ca8') },
            { at: 1, color: hex('#5cc8ff') },
          ],
        },
      },
    ],
    shadows: [{ offsetX: 0, offsetY: 1, blur: 2, color: hex('#000000') }],
  },
};

const AURORA: OverlayAtmosphere = {
  baseColor: '#04070c',
  baseLayers: 'radial-gradient(ellipse 120% 60% at 50% 0%, rgba(45,212,191,0.10), transparent 65%)',
  layers: 'repeating-linear-gradient(95deg, rgba(16,185,129,0) 0px, rgba(45,212,191,0.18) 120px, rgba(34,211,238,0.10) 200px, rgba(16,185,129,0) 320px)',
  layers2: 'repeating-linear-gradient(88deg, rgba(34,211,238,0) 0px, rgba(34,211,238,0.12) 90px, rgba(139,92,246,0.10) 160px, rgba(34,211,238,0) 240px)',
  chatEdge: 'linear-gradient(to bottom, transparent, rgba(45,212,191,0.85) 22%, rgba(34,211,238,0.75) 52%, rgba(139,92,246,0.6) 80%, transparent)',
};

const EMOTE = (content: string, id: string) => ({
  type: 'emote' as const,
  content,
  emote_id: id,
  emote_url: `https://static-cdn.jtvnw.net/emoticons/v2/${id}/default/dark/3.0`,
});

const now = () => new Date().toISOString();

export const DEMO_MESSAGES: OverlayMessage[] = [
  {
    id: 'd1', user_id: '1', username: 'soda', display_name: 'sodapoppin', color: '#e0457b',
    badges: [{ name: 'broadcaster', version: '1', image_url_4x: 'https://static-cdn.jtvnw.net/badges/v1/5527c58c-fb7d-422d-b71b-f309dcb85cc1/3' }],
    timestamp: now(), content: 'painted name, badge, and an emote', provider: 'twitch', channel: 'demo', tags: {},
    segments: [{ type: 'text', content: 'painted name, badge, and an emote ' }, EMOTE('Kappa', '25')],
    metadata: {},
    paint: NEBULA,
  },
  {
    id: 'd2', user_id: '2', username: 'plain', display_name: 'PlainUser', color: '#1e90ff',
    badges: [{ name: 'moderator', version: '1', image_url_4x: 'https://static-cdn.jtvnw.net/badges/v1/3267646d-33f0-4b17-b3df-f923a41db1d0/3' }],
    timestamp: now(), content: 'a normal message with two emotes back to back', provider: 'twitch', channel: 'demo', tags: {},
    segments: [{ type: 'text', content: 'normal message ' }, EMOTE('PogChamp', '305954156'), { type: 'text', content: ' ' }, EMOTE('LUL', '425618')],
    metadata: {},
  },
  {
    id: 'd3', user_id: '3', username: 'winters', display_name: 'Winters', color: '#a98bff',
    badges: [], timestamp: now(), content: 'a StreamNook member with an atmosphere wash', provider: 'twitch', channel: 'demo', tags: {},
    segments: [{ type: 'text', content: 'a StreamNook member with an atmosphere wash' }],
    metadata: {},
    paint: NEBULA,
    streamNookUserNumber: 42,
    atmosphere: AURORA,
  },
  {
    id: 'd4', user_id: '4', username: 'gifter', display_name: 'GifterPro', color: '#7bd88f',
    badges: [], timestamp: now(), content: 'thanks for the year!', provider: 'twitch', channel: 'demo', tags: {},
    segments: [{ type: 'text', content: 'thanks for the year!' }],
    metadata: { msg_type: 'resub', system_message: "GifterPro subscribed at Tier 1. They've subscribed for 12 months!" },
  },
  {
    // Exercises avatars + the leading @ (a YouTube handle) for the
    // showAvatars / showAtSign toggles.
    id: 'd11', user_id: '11', username: '@ytviewer', display_name: '@YTViewer', color: '#ff5c5c',
    badges: [], timestamp: now(), content: 'youtube handle with an avatar', provider: 'youtube', channel: 'youtube:demo',
    tags: { avatar: 'https://static-cdn.jtvnw.net/jtv_user_pictures/xarth/404_user_70x70.png' },
    segments: [{ type: 'text', content: 'youtube handle with an avatar' }],
    metadata: {},
  },
  {
    // Exercises the "Replying to" context line for the showReplies toggle.
    id: 'd12', user_id: '12', username: 'replier', display_name: 'Replier', color: '#ffab40',
    badges: [], timestamp: now(), content: 'this is a reply', provider: 'twitch', channel: 'demo', tags: {},
    segments: [{ type: 'text', content: 'this is a reply' }],
    metadata: {
      reply_info: {
        parent_msg_id: 'd1', parent_display_name: 'sodapoppin', parent_msg_body: 'painted name, badge, and an emote',
        parent_user_id: '1', parent_user_login: 'soda',
      },
    },
  },
  {
    // Exercises the first-time chatter outline for the highlightFirstTime toggle.
    id: 'd13', user_id: '13', username: 'firsttimer', display_name: 'FirstTimer', color: '#5cc8ff',
    badges: [], timestamp: now(), content: 'first message ever in this channel', provider: 'twitch', channel: 'demo',
    tags: { 'first-msg': '1' },
    segments: [{ type: 'text', content: 'first message ever in this channel' }],
    metadata: { is_first_message: true },
  },
  {
    // Exercises a /me action carrying a Twitch emote after a name.
    id: 'd14', user_id: '14', username: 'zoox', display_name: 'Zo0x_', color: '#c792ea',
    badges: [], timestamp: now(), content: 'was slain by Jul00s Kappa', provider: 'twitch', channel: 'demo', tags: {},
    segments: [
      { type: 'text', content: 'was slain by Jul00s ' },
      { type: 'emote', content: 'Kappa', emote_id: '25', emote_url: 'https://static-cdn.jtvnw.net/emoticons/v2/25/default/dark/3.0' },
    ],
    metadata: { is_action: true },
  },
  {
    // Exercises a zero-width 7TV emote stacked on the one before it, and a navy
    // name for Readable name colors.
    id: 'd17', user_id: '17', username: 'rainyday', display_name: 'RainyDay', color: '#1a237e',
    badges: [], timestamp: now(), content: 'forecast says peepoHappy RainTime', provider: 'twitch', channel: 'demo', tags: {},
    segments: [
      { type: 'text', content: 'forecast says ' },
      { type: 'emote', content: 'peepoHappy', emote_id: '01KTR4A3Z08TPFNFA5CRVM9319', emote_url: 'https://cdn.7tv.app/emote/01KTR4A3Z08TPFNFA5CRVM9319/3x.webp' },
      { type: 'text', content: ' ' },
      { type: 'emote', content: 'RainTime', emote_id: '01FCY771D800007PQ2DF3GDTN6', emote_url: 'https://cdn.7tv.app/emote/01FCY771D800007PQ2DF3GDTN6/3x.webp', is_zero_width: true },
    ],
    metadata: {},
  },
  redeem('d15', 'MathoX', '#ff4d4d', 'Main gauche en jeu', 60000),
  redeem('d16', 'HydroHomie', '#4fc3f7', 'Hydrate', 5000, 'https://static-cdn.jtvnw.net/custom-reward-images/default-4.png', '#00C7AC'),
  ev('d5', 'Bomber', '#ff6a3c', 'submysterygift', "Bomber is gifting 10 Tier 1 Subs to the community! They've gifted 120 in the channel!"),
  ev('d6', 'RaiderTV', '#67c2ff', 'raid', '15 raiders from RaiderTV have joined!'),
  ev('d7', 'CheerFan', '#ffd166', 'cheer', 'CheerFan cheered 500 bits!'),
  ev('d8', 'Streaker', '#a3e635', 'viewermilestone', 'Streaker watched 20 streams in a row and shared a Watch Streak!'),
  ev('d9', 'NewFollow', '#f472b6', 'follow', 'NewFollow followed!'),
  ev('d10', 'Broadcaster', '#c792ea', 'announcement', 'This is a channel announcement!'),
];

// A no-text channel-point redemption, tagged the way twitchClient builds one.
function redeem(id: string, name: string, color: string, title: string, cost: number, image?: string, bg?: string): OverlayMessage {
  const tags: Record<string, string> = { 'sn-reward-title': title, 'sn-reward-cost': String(cost) };
  if (image) tags['sn-reward-image'] = image;
  if (bg) tags['sn-reward-bg'] = bg;
  return {
    id, user_id: id, username: name.toLowerCase(), display_name: name, color,
    badges: [], timestamp: now(), content: '', provider: 'twitch', channel: 'demo', tags,
    segments: [], metadata: {},
  };
}

// A bodiless event message of a given type (system message only).
function ev(id: string, name: string, color: string, msgType: string, systemMessage: string): OverlayMessage {
  return {
    id, user_id: id, username: name.toLowerCase(), display_name: name, color,
    badges: [], timestamp: now(), content: '', provider: 'twitch', channel: 'demo', tags: {},
    segments: [], metadata: { msg_type: msgType, system_message: systemMessage },
  };
}
